/**
 * Account API Cache Utility
 *
 * Prevents multiple API calls when multiple widgets are on the same page.
 *
 * Features:
 * - In-memory cache per page load (fast access)
 * - localStorage cache with 1-hour TTL (persists across page loads)
 * - Race condition protection: Only 1 API call per account, even with simultaneous requests
 * - WordPress integration: Auto-detects WordPress and fetches from server cache
 */

// Simple logger for non-widget contexts (won't use logger.js as this is a shared utility)
const isDev = typeof window !== 'undefined' && (
  window.location?.hostname === 'localhost' ||
  window.location?.hostname === '127.0.0.1' ||
  window.location?.port === '5173' ||
  window.location?.port === '3000'
);
const cacheLog = (...args) => { if (isDev) console.log(...args); };
const cacheWarn = (...args) => { if (isDev) console.warn(...args); };
const cacheError = (...args) => { console.error(...args); }; // Always log errors

// In-memory cache for current page load only
// Use window global to share state across multiple widget bundles
if (!window.__nexusAccountCache) {
  window.__nexusAccountCache = new Map();
}
if (!window.__nexusAccountFetches) {
  window.__nexusAccountFetches = new Map();
}

const accountCache = window.__nexusAccountCache;
const pendingFetches = window.__nexusAccountFetches;

// Cache duration: 1 hour (3600000 ms) - optimized for performance
const CACHE_DURATION = 60 * 60 * 1000;

/**
 * Detect if running in WordPress environment
 * @returns {boolean} - True if WordPress is detected
 */
function isWordPressEnvironment() {
  // Check for WordPress-specific global variables
  return (
    typeof window !== 'undefined' &&
    (
      typeof window.wp !== 'undefined' || // WordPress wp object
      typeof window.wpApiSettings !== 'undefined' || // WordPress API settings
      document.body?.classList?.contains('wordpress') || // Custom class
      document.querySelector('meta[name="generator"]')?.content?.includes('WordPress') || // WP meta tag
      typeof window.nexusPluginData !== 'undefined' // Custom plugin indicator
    )
  );
}

/**
 * Get WordPress REST API base URL
 * @returns {string|null} - WordPress REST API URL or null if not available
 */
function getWordPressApiUrl() {
  // Check for wpApiSettings (set by WordPress)
  if (typeof window.wpApiSettings !== 'undefined' && window.wpApiSettings.root) {
    return window.wpApiSettings.root.replace(/\/+$/, '');
  }

  // Check for custom nexus plugin data
  if (typeof window.nexusPluginData !== 'undefined' && window.nexusPluginData.restUrl) {
    return window.nexusPluginData.restUrl.replace(/\/+$/, '');
  }

  // Fallback: try common WordPress REST API path
  const currentOrigin = window.location.origin;
  return `${currentOrigin}/wp-json`;
}

/**
 * Get WordPress cache file URL for an account
 * @param {string} accountId - Account ID
 * @returns {string|null} - Cache file URL or null if not available
 */
function getWordPressCacheFileUrl(accountId) {
  // Check for custom nexus plugin data with cache URL
  if (typeof window.nexusPluginData !== 'undefined' && window.nexusPluginData.cacheUrl) {
    const cacheUrl = window.nexusPluginData.cacheUrl.replace(/\/+$/, '');
    return `${cacheUrl}/account_${accountId}.json`;
  }

  // Fallback: try common uploads path
  const currentOrigin = window.location.origin;
  return `${currentOrigin}/wp-content/uploads/nexus-cache/account_${accountId}.json`;
}

/**
 * Get cached data from localStorage
 * @param {string} cacheKey - The cache key
 * @returns {object|null} - Cached data or null if expired/not found
 */
function getFromLocalStorage(cacheKey) {
  try {
    const stored = localStorage.getItem(`nexus_account_${cacheKey}`);
    if (!stored) return null;

    const { data, timestamp } = JSON.parse(stored);
    const now = Date.now();

    // Check if cache is still valid (within 1 hour)
    if (now - timestamp < CACHE_DURATION) {
      return data;
    }

    // Cache expired, remove it
    localStorage.removeItem(`nexus_account_${cacheKey}`);
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Save data to localStorage cache
 * @param {string} cacheKey - The cache key
 * @param {object} data - The data to cache
 */
function saveToLocalStorage(cacheKey, data) {
  try {
    const payload = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(`nexus_account_${cacheKey}`, JSON.stringify(payload));
  } catch (error) {
    // Silently fail if localStorage is full or unavailable
    cacheWarn('[AccountCache] Failed to save to localStorage:', error);
  }
}

/**
 * Get cached account data or fetch from API
 * @param {string} accountId - The account ID to fetch
 * @param {string} apiOrigin - The API origin URL (optional, defaults to https://api.nexus.design)
 * @returns {Promise<object|null>} - The account data or null if error
 */
export async function getCachedAccount(accountId, apiOrigin = "https://api.nexus.design") {
  if (!accountId) return null;

  const cacheKey = `${accountId}_${apiOrigin}`;

  try {
    // 1. Check in-memory cache first (fastest)
    if (accountCache.has(cacheKey)) {
      cacheLog(`[AccountCache] ⚡ Using in-memory cache for account ${accountId}`);
      return accountCache.get(cacheKey);
    }

    // 2. Check localStorage cache (fast, persists across page loads)
    const cachedData = getFromLocalStorage(cacheKey);
    if (cachedData) {
      cacheLog(`[AccountCache] 💾 Using localStorage cache for account ${accountId}`);
      // Also store in memory for faster subsequent access
      accountCache.set(cacheKey, cachedData);
      return cachedData;
    }

    // 3. Check if a fetch is already in progress
    if (pendingFetches.has(cacheKey)) {
      cacheLog(`[AccountCache] ⏳ Waiting for in-flight request for account ${accountId}`);
      return await pendingFetches.get(cacheKey);
    }

    // 4. Start new fetch and store the promise
    const isWordPress = isWordPressEnvironment();
    const fetchSource = isWordPress ? 'WordPress cache' : 'Nexus API';
    cacheLog(`[AccountCache] 🌐 Fetching fresh data for account ${accountId} from ${fetchSource}`);

    const fetchPromise = (async () => {
      try {
        let res;

        // If in WordPress, try to fetch from static cache file first (NO API CALL!)
        if (isWordPress) {
          const cacheFileUrl = getWordPressCacheFileUrl(accountId);

          cacheLog(`[AccountCache] 📁 WordPress detected, fetching from static file: ${cacheFileUrl}`);

          try {
            // Try fetching static JSON file directly
            res = await fetch(cacheFileUrl, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'same-origin',
            });

            if (res.ok) {
              cacheLog(`[AccountCache] ✅ Successfully fetched from WordPress static cache file`);
              // Parse the file data which has metadata wrapper
              const fileData = await res.json();

              // Check if cache is still valid
              if (fileData.expires_at && fileData.expires_at > Math.floor(Date.now() / 1000)) {
                cacheLog(`[AccountCache] ✓ Cache file is valid until ${new Date(fileData.expires_at * 1000).toLocaleString()}`);

                // Return the actual account data
                const data = fileData.data || fileData;
                accountCache.set(cacheKey, data);
                saveToLocalStorage(cacheKey, data);
                return data;
              } else {
                cacheWarn(`[AccountCache] ⚠️ Cache file expired, falling back to Nexus API`);
              }
            } else {
              cacheWarn(`[AccountCache] ⚠️ Cache file not found (${res.status}), falling back to Nexus API`);
            }
          } catch (fileError) {
            cacheWarn(`[AccountCache] ⚠️ Failed to fetch cache file:`, fileError.message, '- falling back to Nexus API');
          }

          // Fall back to Nexus API if static file fetch failed
          res = await fetch(`${apiOrigin.replace(/\/+$/, "")}/api/accounts/${accountId}`);
        } else {
          // Not in WordPress, fetch directly from Nexus API
          res = await fetch(`${apiOrigin.replace(/\/+$/, "")}/api/accounts/${accountId}`);
        }

        if (!res.ok) {
          cacheError(`[AccountCache] API error: ${res.status} ${res.statusText}`);
          return null;
        }

        const data = await res.json().catch(() => ({}));

        // Cache the result in both memory and localStorage
        accountCache.set(cacheKey, data);
        saveToLocalStorage(cacheKey, data);

        return data;
      } finally {
        // Clean up the pending fetch promise
        pendingFetches.delete(cacheKey);
      }
    })();

    // Store the promise so other concurrent calls can wait for it
    pendingFetches.set(cacheKey, fetchPromise);

    return await fetchPromise;
  } catch (error) {
    cacheError(`[AccountCache] Error fetching account ${accountId}:`, error);
    pendingFetches.delete(cacheKey);
    return null;
  }
}

/**
 * Clear cached account data for a specific account
 * @param {string} accountId - The account ID to clear from cache
 */
export function clearAccountCache(accountId) {
  if (!accountId) return;

  let cleared = 0;
  // Clear all in-memory cache entries for this account (any apiOrigin)
  for (const key of accountCache.keys()) {
    if (key.startsWith(`${accountId}_`)) {
      accountCache.delete(key);
      cleared++;
    }
  }

  // Clear from localStorage
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`nexus_account_${accountId}_`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    cleared += keysToRemove.length;
  } catch (error) {
    // Ignore localStorage errors
  }

  cacheLog(`[AccountCache] Cleared ${cleared} cache entries for account ${accountId}`);
}

/**
 * Clear all cached account data
 */
export function clearAllAccountCache() {
  const size = accountCache.size;
  accountCache.clear();

  // Clear all from localStorage
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('nexus_account_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    // Ignore localStorage errors
  }

  cacheLog(`[AccountCache] Cleared ${size} cached accounts`);
}

/**
 * Get cache info for debugging
 * @returns {Array} - Array of cached account info
 */
export function getCacheInfo() {
  const info = [];

  for (const [key] of accountCache.entries()) {
    const parts = key.split('_');
    const accountId = parts[0];
    const apiOrigin = parts.slice(1).join('_');

    info.push({
      accountId,
      apiOrigin,
      cacheKey: key
    });
  }

  return info;
}

/**
 * React Hook for using account cache
 * Provides loading state, error handling, and auto-refresh capability
 *
 * Usage:
 * const { data, loading, error, refresh } = useAccountCache(accountId);
 *
 * @param {string} accountId - The account ID to fetch
 * @param {string} apiOrigin - The API origin URL (optional)
 * @returns {{ data: object|null, loading: boolean, error: Error|null, refresh: () => void }}
 */
export function useAccountCache(accountId, apiOrigin = "https://api.nexus.design") {
  // Using a simple state pattern that works in both widget bundles and main app
  // This avoids circular dependencies with React imports
  const stateRef = { current: { data: null, loading: true, error: null } };
  const listeners = new Set();

  const getState = () => stateRef.current;
  const setState = (newState) => {
    stateRef.current = { ...stateRef.current, ...newState };
    listeners.forEach(l => l(stateRef.current));
  };

  const fetchData = async () => {
    if (!accountId) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    setState({ loading: true, error: null });

    try {
      const data = await getCachedAccount(accountId, apiOrigin);
      setState({ data, loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: err });
    }
  };

  // Initial fetch
  fetchData();

  return {
    get data() { return getState().data; },
    get loading() { return getState().loading; },
    get error() { return getState().error; },
    refresh: fetchData,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
}
